import React, { useState, useEffect } from "react";
import SidebarLayout from "../../common/sidebar/SidebarLayout";
import { Helmet } from "react-helmet";
import PatientLoader from "../../common/PatientLoader";
import { useDispatch, useSelector } from "react-redux";
import OPSPrimaryHeader from "../../common/primaryHeader/OPSPrimaryHeader";
import { InfoCard } from "../dashboard/DashboardHome";
import { useNavigate } from "react-router-dom";
import "./OPSStyles.css";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/TestAccounts.svg";
import {
  downloadHealthcareDataCSV,
  fetchHealthCareGroupsLogic,
  searchClinicsLogic,
} from "../../../store/features/ops/opsSlice";
import HomeDataTable from "./HomeTable/HomeDataTable";
import { getFullName, notify } from "../../../util/helpers";
import { patientLogic } from "../../../store/features/patient/patientSlice";

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, user } = useSelector((state) => state.app);
  const { opsLoading, healthcareGroups, allClinics } = useSelector(
    (state) => state.ops
  );
  const [onlyCardPayment, setOnlyCardPayment] = useState(true);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [pendingActionsOnly, setPendingActionsOnly] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [expiringCards, setExpiringCards] = useState([]);
  const [noCards, setNoCards] = useState([]);

  const handlePaymentMethodToggle = () => {
    setPaymentMethodLoading(true);
    setOnlyCardPayment(!onlyCardPayment);
    setPaymentMethodLoading(false);
  };

  const handleExportMode = () => {
    setExportMode(!exportMode);
  };

  const handlePendingAction = () => {
    setPendingActionsOnly(!pendingActionsOnly);
  };

  const handleNavigation = (navigate_to) => {
    navigate(navigate_to);
  };

  function getCardData() {
    //TODO: Refactor this
    const expiringGroups = [];
    const noCardGroups = [];
    if (healthcareGroups.length > 0) {
      healthcareGroups.forEach((x) => {
        if (x.invoicing && x.invoicing.card_expiry_alert) {
          if (onlyCardPayment && x.payment_type === "Card") {
            expiringGroups.push({
              id: x.id,
              name: x.name,
              year: x.invoicing.card_expiration_year,
              month: x.invoicing.card_expiration_month,
              payment_type: x.payment_type,
            });
          } else if (!onlyCardPayment) {
            expiringGroups.push({
              id: x.id,
              name: x.name,
              year: x.invoicing.card_expiration_year,
              month: x.invoicing.card_expiration_month,
              payment_type: x.payment_type,
            });
          }
        }
        if (x.invoicing && x.invoicing.is_card_info_missing) {
          if (onlyCardPayment && x.payment_type === "Card") {
            noCardGroups.push({
              id: x.id,
              name: x.name,
              payment_type: x.payment_type,
            });
          } else if (!onlyCardPayment) {
            noCardGroups.push({
              id: x.id,
              name: x.name,
              payment_type: x.payment_type,
            });
          }
        }
      });
    }

    setExpiringCards(expiringGroups);
    setNoCards(noCardGroups);
  }

  useEffect(() => {
    dispatch(fetchHealthCareGroupsLogic({ is_test_account: 0, search: "" }));
    dispatch(searchClinicsLogic({ search: "" }));
  }, []);

  useEffect(() => {
    if (!opsLoading) {
      getCardData();
    }
  }, [opsLoading, onlyCardPayment]);

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadCSVData = (months) => {
    if (isDownloading) {
      notify("File generation is in progress, please wait..", "info");
      return;
    }
    notify("Your file will be ready shortly..", "info");
    downloadHealthcareDataCSV()
      .then((res) => {
        if (res.status === 200) {
          window.open(res?.data?.url);
        }
        setIsDownloading(false);
      })
      .catch((e) => {
        console.log(e);
        notify("Something went wrong! Try again later!", "error");
        setIsDownloading(false);
      });
  };

  return (
    <SidebarLayout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>OPS Dashboard | Rose Health</title>
        <link rel="canonical" href={window.location.origin} />
      </Helmet>
      {loading ? (
        <PatientLoader />
      ) : (
        <section className="container-fluid px-5 py-2">
          <OPSPrimaryHeader
            titleText={`Hey ${getFullName(user)},`}
            showSubtitle={true}
            subTitleText="These alerts require your attention"
          />
          <div className="d-flex my-2" style={{ gap: "10px" }}>
            <label className="switch-small">
              <input
                disabled={paymentMethodLoading}
                onChange={() => {
                  handlePaymentMethodToggle();
                }}
                checked={onlyCardPayment}
                type="checkbox"
              />
              <span className="slider-small round"></span>
            </label>
            <span className="muted-text">
              Consider payment method 'Card' only
            </span>
          </div>
          {loading || opsLoading ? (
            <PatientLoader />
          ) : (
            <section
              className="container-fluid w-100"
              style={{ margin: "0px", padding: "0px" }}
            >
              <div
                className="container-fluid px-1 py-4 row"
                style={{ margin: "0px" }}
              >
                <div
                  className="col-sm-12 col-md-3 col-lg-3"
                  style={{ margin: "0px", padding: "0px" }}
                >
                  <InfoCard
                    title={"Total Cards"}
                    button_link={"/cards/all/"}
                    button_text={"View All Cards"}
                    stats={healthcareGroups?.length || 0}
                    information={"View All Cards Information"}
                    handleNavigation={handleNavigation}
                  />
                </div>
                <div className="col-sm-12 col-md-3 col-lg-3">
                  <InfoCard
                    title={"Missing Card Information"}
                    button_link={"/cards/missing/"}
                    button_text={"View Missing Cards"}
                    stats={noCards?.length || 0}
                    information={"View All Missing Cards Information"}
                    handleNavigation={handleNavigation}
                    style={{ backgroundColor: "#f2f6fc", color: "#044CB9" }}
                  />
                </div>
                <div className="col-sm-12 col-md-3 col-lg-3">
                  <InfoCard
                    title={"Expiring Soon"}
                    button_link={"/cards/expiring/"}
                    button_text={"View Expiring Cards"}
                    stats={expiringCards?.length || 0}
                    information={"View All Cards that are expiring soon"}
                    handleNavigation={handleNavigation}
                    style={{ backgroundColor: "#fff4e6", color: "#C46F00" }}
                  />
                </div>
                <div className="col-sm-12 col-md-3 col-lg-3">
                  <InfoCard
                    title={"Closed Cards"}
                    button_link={"/cards/closed/"}
                    button_text={"View Closed Cards"}
                    stats={expiringCards?.length || 0}
                    information={"View All Closed Cards"}
                    handleNavigation={handleNavigation}
                    style={{ backgroundColor: "#ffeceb", color: "#F04438" }}
                  />
                </div>
              </div>

              <div className="container-fluid px-3">
                <OPSPrimaryHeader
                  titleText={`All Healthcare Groups`}
                  showSubtitle={false}
                  showButton={false}
                  showPill={true}
                  pillText={`${healthcareGroups?.length || 0} Groups`}
                />
                <div className="d-flex justify-content-between my-4">
                  <div className="d-flex" style={{ gap: "30px" }}>
                    <div className="d-flex my-2" style={{ gap: "10px" }}>
                      <label className="switch-small">
                        <input
                          disabled={false}
                          onChange={() => {
                            handlePendingAction();
                          }}
                          checked={pendingActionsOnly}
                          type="checkbox"
                        />
                        <span className="slider-small round"></span>
                      </label>
                      <span className="muted-text">
                        Show only groups with Pending Actions
                      </span>
                    </div>
                    <div className="d-flex my-2" style={{ gap: "10px" }}>
                      <label className="switch-small">
                        <input
                          disabled={false}
                          onChange={() => {
                            handleExportMode();
                          }}
                          checked={exportMode}
                          type="checkbox"
                        />
                        <span className="slider-small round"></span>
                      </label>
                      <span className="muted-text">Export mode</span>
                    </div>
                  </div>
                  <button
                    className="primary-btn d-flex"
                    style={{ gap: "10px" }}
                    disabled={isDownloading}
                    onClick={() => {
                      downloadCSVData();
                    }}
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>
              </div>
              <div className="container-fluid">
                {opsLoading ? <PatientLoader /> : <HomeDataTable />}
              </div>
            </section>
          )}
        </section>
      )}
    </SidebarLayout>
  );
};

export default Home;
